/* ============================================================
   evaya® — Mattress finder, shared logic
   Loaded by sections/mattress-finder.liquid and
   sections/mattress-finder-module.liquid. Both sections own their
   own markup and CSS; everything below is view-agnostic.

   NOTE: there is deliberately no evaya-mattress-finder.js.liquid.
   A plain .js and a .js.liquid of the same name shadow each other
   in Shopify and the .liquid silently stops serving. Keep it that
   way — this file needs no Liquid.

   The section passes in SHOP, a map keyed Youth/Pure/Original/
   Bliss/Envy, each entry emitted from the live product:

     { handle, url, title, available, from, fromCents,
       image, imageAlt, height, comingSoon, firmnessLabel,
       optionNames: ["Size","Firmness","Shipping"],
       variants: [{ id, options, available, price, cents }] }

   Nothing about the catalogue is hardcoded here. Heights, feels,
   sizes, prices, availability and coming-soon all come from the
   product and its custom.* metafields, so this stays correct when
   the catalogue changes.
   ============================================================ */
(function (w) {
  'use strict';

  var FREE_SHIPPING = 'Free Shipping';

  /* ── small helpers ───────────────────────────────────── */

  function uniq(list) {
    var out = [];
    list.forEach(function (x) { if (x != null && out.indexOf(x) < 0) out.push(x); });
    return out;
  }

  function idxOf(d, name) {
    return (d && d.optionNames) ? d.optionNames.indexOf(name) : -1;
  }

  /* "an 8-inch", "a 12-inch". Spoken, 8 / 11 / 18 take "an". */
  function article(height) {
    var m = String(height).match(/^(\d+)/);
    if (!m) return 'a';
    return /^(8|11|18)$/.test(m[1]) ? 'an' : 'a';
  }

  /* Every size this product is actually built in. */
  function sizesOf(d) {
    var i = idxOf(d, 'Size');
    if (!d || !d.variants || i < 0) return [];
    return uniq(d.variants.map(function (v) { return v.options[i]; }));
  }

  /* Every firmness this product is actually built in. Products with
     no Firmness option (Youth, Pure) report their single feel from
     the custom.firmness_label metafield instead. */
  function feelsOf(d) {
    var i = idxOf(d, 'Firmness');
    if (!d) return [];
    if (i < 0) return d.firmnessLabel ? [d.firmnessLabel] : [];
    return uniq(d.variants.map(function (v) { return v.options[i]; }));
  }

  /* ── variant matching ────────────────────────────────────
     Matches on all three options by NAME, never by position, and
     always prefers Free Shipping. The +$199 install variant is
     only ever reachable if no free-shipping variant exists for
     that size and feel, in which case we take the cheapest match
     rather than whichever happens to be first in the array.
     ──────────────────────────────────────────────────────── */
  function findVariant(d, firmness, size) {
    if (!d || !d.variants || !d.variants.length) return null;

    var si = idxOf(d, 'Size');
    var fi = idxOf(d, 'Firmness');
    var pi = idxOf(d, 'Shipping');
    if (si < 0) return null;

    var matches = d.variants.filter(function (v) {
      if (v.options[si] !== size) return false;
      if (fi > -1 && v.options[fi] !== firmness) return false;
      return true;
    });
    if (!matches.length) return null;

    var free = matches.filter(function (v) {
      return pi < 0 || v.options[pi] === FREE_SHIPPING;
    });
    var pool = free.length ? free : matches;

    /* Prefer an available variant, then the cheapest. */
    var live = pool.filter(function (v) { return v.available; });
    var take = live.length ? live : pool;
    return take.slice().sort(function (a, b) {
      return (a.cents || 0) - (b.cents || 0);
    })[0] || null;
  }

  /* ── the questions ───────────────────────────────────────
     Sizes and prices are built from live product data. `cols` is a
     layout hint; each section clamps it to what its own grid can
     take.
     ──────────────────────────────────────────────────────── */

  /* Long option values stay canonical for matching; only the button
     label is shortened. "California King" must never become
     "Cal King" in the value — the mattresses are not built under
     that name and the match would silently fail. */
  var SIZE_LABEL = { 'California King': 'Cal King' };

  var SIZE_ORDER = ['Twin', 'Twin XL', 'Full', 'Queen', 'King', 'California King'];

  function allSizes(shop) {
    var seen = [];
    ['Youth', 'Pure', 'Original', 'Bliss', 'Envy'].forEach(function (k) {
      sizesOf(shop[k]).forEach(function (s) { if (seen.indexOf(s) < 0) seen.push(s); });
    });
    if (!seen.length) return SIZE_ORDER.slice();
    return seen.sort(function (a, b) {
      var ia = SIZE_ORDER.indexOf(a), ib = SIZE_ORDER.indexOf(b);
      return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib);
    });
  }

  function priceHint(d) {
    return (d && d.from) ? 'From ' + d.from : '';
  }

  function questions(shop) {
    shop = shop || {};
    return [
      { id: 'position', num: '01', cols: 4,
        q: 'How do you <em>sleep</em>?',
        help: 'Pick the position you spend most of the night in.',
        options: [
          { v: 'side',        label: 'On my side',        sub: 'Shoulders and hips take the load' },
          { v: 'back',        label: 'On my back',        sub: 'Spine wants even lift' },
          { v: 'stomach',     label: 'On my stomach',     sub: 'Hips shouldn’t sink' },
          { v: 'combination', label: 'A bit of everything', sub: 'You move through the night' }
        ] },

      { id: 'who', num: '02', cols: 3,
        q: 'Who’s it <em>for</em>?',
        help: 'This changes which build we point you to.',
        options: [
          { v: 'adult', label: 'An adult or a couple', sub: 'The main bed' },
          { v: 'child', label: 'A child or teen',      sub: 'Growing bodies, lighter frames' },
          { v: 'guest', label: 'A guest room',         sub: 'Occasional use, all shapes of guest' }
        ] },

      { id: 'feel', num: '03', cols: 4,
        q: 'How do you like it to <em>feel</em>?',
        help: 'Pick what sounds right — “not sure” is a perfectly good answer.',
        options: [
          { v: 'softer',  label: 'Softer',   sub: 'More give, more cradle',     glyph: 'plush' },
          { v: 'medium',  label: 'Medium',   sub: 'Balanced, in the middle',    glyph: 'medium' },
          { v: 'firmer',  label: 'Firmer',   sub: 'More lift, less sink',       glyph: 'firm' },
          { v: 'notsure', label: 'Not sure', sub: 'We’ll point you to Medium', glyph: 'unsure' }
        ] },

      { id: 'size', num: '04', cols: 3,
        q: 'What <em>size</em> are you after?',
        help: 'We carry every standard size in every build.',
        options: allSizes(shop).map(function (s) {
          return { v: s, label: SIZE_LABEL[s] || s };
        }) },

      { id: 'tier', num: '05', cols: 4,
        q: 'What matters most <em>to you</em>?',
        help: 'Every build shares the same certified organic materials.',
        options: [
          { v: 'entry',     label: 'I&rsquo;m on a budget',
            sub: (shop.Pure && shop.Pure.blurb) || 'Our simplest certified-organic build, in a single perfected feel.',
            hint: priceHint(shop.Pure) },
          /* The four labels are four different PRIORITIES, not four price points:
             price -> practicality -> comfort -> nothing-but-the-best.

             Original is deliberately the least seductive of the four.
             "Everyday" signals not-special on purpose. Any bestseller or
             most-popular framing here is the strongest available lever for
             pulling shoppers DOWN from Bliss ($2,499) to Original ($1,499), so
             it is ruled out however well it tests. Rejected for the same
             reason: "the one most people choose", "the best all-rounder".

             Bliss is framed as a decision already made rather than an
             indulgence — "I want to be spoiled" invited second-guessing at
             checkout on the highest-margin mattress. Kept clear of a
             superlative so it does not collide with Envy's "the best you make".

             Envy avoids a superlative. "The best you make" demoted the other
             four models by implication, which is a real problem for a range
             where every bed is premium and organic. "Nothing but luxury" was
             considered and rejected for the same reason: it keeps the ladder
             and only repaints the top rung, and it collides with Bliss, which
             owns "luxury" in its own product copy. "Premium build" describes
             the construction rather than ranking the outcomes.

             Also rejected: "I know what I'm looking for" (describes the
             shopper's knowledge, not a want, and self-contradicting inside a
             quiz) and "I'm upgrading to organic" (all five beds are organic,
             so on Original it implies Pure is not). 2026-09-03, w/ Lyle. */
          { v: 'signature', label: 'I want a great everyday bed',
            sub: (shop.Original && shop.Original.blurb) || 'Our most-loved hybrid — support, bounce and breathability in balance.',
            hint: priceHint(shop.Original) },
          { v: 'premium',   label: 'Comfort is the priority',
            sub: (shop.Bliss && shop.Bliss.blurb) || 'Cashmere-finished luxury over a zoned innerspring — a true pillow-top.',
            hint: priceHint(shop.Bliss) },
          { v: 'flagship',  label: 'I want your most premium build',
            sub: (shop.Envy && shop.Envy.blurb) || 'All latex, zero coils — the only Evaya with no spring unit anywhere.',
            hint: priceHint(shop.Envy) }
        ] }
    ];
  }

  /* ── firmness ────────────────────────────────────────── */

  var FEEL_REASON = {
    Plush:  'a softer top that lets shoulders and hips settle in.',
    Medium: 'cushion and support in even measure.',
    Firm:   'more lift and less sink, so the spine stays level.'
  };
  var POS_REASON = {
    side:        'Side sleeping asks for pressure relief at the shoulders and hips.',
    back:        'Back sleeping asks for even lift through the spine.',
    stomach:     'Stomach sleeping asks for a firm, level surface.',
    combination: 'Moving between positions asks for a balanced feel.'
  };

  function idealFeel(position, feel) {
    if (feel === 'notsure' || feel === 'medium') return 'Medium';
    var base = ({ side: 1, combination: 2, back: 2.6, stomach: 3 })[position] || 2;
    var score = base + (feel === 'softer' ? -0.75 : feel === 'firmer' ? 0.75 : 0);
    if (score < 1.75) return 'Plush';
    if (score < 2.5) return 'Medium';
    return 'Firm';
  }

  /* ── routing ─────────────────────────────────────────── */

  function pick(answers) {
    if (answers.who === 'child') return 'Youth';
    if (answers.who === 'guest') {
      return (answers.tier === 'premium' || answers.tier === 'flagship') ? 'Original' : 'Pure';
    }
    if (answers.tier === 'entry')    return 'Pure';
    if (answers.tier === 'flagship') return 'Envy';
    if (answers.tier === 'premium')  return 'Bliss';
    return 'Original';
  }

  function resolve(answers, shop) {
    shop = shop || {};
    var ideal = idealFeel(answers.position, answers.feel);
    var key = pick(answers);
    var d = shop[key] || {};
    var note = null;
    var sizes = sizesOf(d);

    /* The product may not be built in the size they asked for —
       Youth is Twin through Queen only. Say so and move them across
       rather than dead-ending on "Currently unavailable". */
    if (sizes.length && sizes.indexOf(answers.size) < 0) {
      var sizeLabel = SIZE_LABEL[answers.size] || answers.size;
      var alt = shop.Original && sizesOf(shop.Original).indexOf(answers.size) > -1 ? 'Original' : null;
      if (alt) {
        note = (d.title || key) + ' is built in ' +
               sizes.map(function (s) { return SIZE_LABEL[s] || s; }).join(', ') +
               ' only. For a ' + sizeLabel + ' we’d point you at the Original.';
        key = alt;
        d = shop[key] || {};
      } else {
        note = (d.title || key) + ' is not built in ' + sizeLabel + '.';
      }
    }

    var feels = feelsOf(d);
    var firmness = feels.length > 1 ? ideal : (feels[0] || d.firmnessLabel || 'Medium');
    var height = d.height || '';
    var name = (d.title || ('Evaya ' + key)).replace(/^Evaya\s+/, '');

    var rationale;
    if (key === 'Youth') {
      rationale = 'Built for growing bodies — ' +
                  (height ? article(height) + ' ' + height + ' profile' : 'a slim profile') +
                  ' with one gentle feel, sized for kids and teens.';
    } else if (key === 'Envy') {
      rationale = POS_REASON[answers.position] + ' Envy answers it with ' +
                  (height ? height + ' of Talalay latex' : 'an all-latex build') +
                  ' and no coil unit at all — the ' + firmness +
                  ' comfort layer gives you ' + FEEL_REASON[firmness];
    } else if (key === 'Pure' && answers.who === 'guest') {
      rationale = 'Guests come in every shape, so the ' +
                  (height ? height + ' ' : '') + 'Pure in ' + firmness +
                  ' is the surest welcome.';
    } else {
      rationale = POS_REASON[answers.position] + ' The ' +
                  (height ? height + ' ' : '') + name + ' in ' + firmness +
                  ' gives you ' + FEEL_REASON[firmness];
    }

    /* Single-feel product, but the sleep answers pointed elsewhere.
       Say it plainly and name the bed that does come in that feel. */
    if (key !== 'Youth' && feels.length === 1 && ideal !== feels[0] && !note) {
      var acrossFeels = feelsOf(shop.Original);
      if (acrossFeels.indexOf(ideal) > -1) {
        note = name + ' is built in one ' + feels[0].toLowerCase() + ' feel. If you’d rather have ' +
               ideal.toLowerCase() + ', the Original in ' + ideal + ' is the next step across.';
      }
    }

    var variant = findVariant(d, firmness, answers.size);

    return {
      key: key,
      model: name,
      title: d.title || ('Evaya ' + key),
      firmness: firmness,
      profile: height,
      size: answers.size,
      sizeLabel: SIZE_LABEL[answers.size] || answers.size,
      comingSoon: !!d.comingSoon,
      rationale: rationale,
      note: note,
      data: d,
      variant: variant,
      /* The price of the exact configuration they picked, not a
         from-price. Sections must label it as such. */
      price: (variant && variant.price) || d.from || ''
    };
  }

  /* ── add to cart ─────────────────────────────────────────
     Evaya's cart drawer exposes no events. It exposes two globals,
     and the refresh must settle before the open or the drawer shows
     stale contents.

     Call this from a plain button, never from a form submit. The
     theme has a delegated submit handler on `document` matching
     form[action$="/cart/add"] that does its own POST — a form here
     would add the item twice.

     opts.openDrawer defaults to true. Pass false from inside a modal
     (the overlay finder) so the drawer is refreshed but not opened —
     otherwise a second overlay stacks behind the first.
     ──────────────────────────────────────────────────────── */
  function addToCart(cartAddUrl, variantId, opts) {
    var openDrawer = !(opts && opts.openDrawer === false);
    return fetch(cartAddUrl + '.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
    })
      .then(function (res) {
        if (!res.ok) return res.json().then(function (e) {
          throw new Error((e && e.description) || 'Add to cart failed');
        }, function () { throw new Error('Add to cart failed'); });
        return res.json();
      })
      .then(function (payload) {
        return Promise.resolve(
          w.evayaRefreshCartDrawer ? w.evayaRefreshCartDrawer() : null
        ).then(function () { return payload; });
      })
      .then(function (payload) {
        if (openDrawer && w.evayaOpenCartDrawer) w.evayaOpenCartDrawer();
        return payload;
      });
  }

  /* ── option glyphs, shared so the two sections can't drift ── */
  var GLYPH = {
    plush:  '<path d="M2 6 Q16 -8 30 6 T58 6 T86 6 T114 6"/><path d="M2 22 Q16 8 30 22 T58 22 T86 22 T114 22"/><path d="M2 38 Q16 24 30 38 T58 38 T86 38 T114 38"/>',
    medium: '<path d="M2 11 Q16 6 30 11 T58 11 T86 11 T114 11"/><path d="M2 22 Q16 17 30 22 T58 22 T86 22 T114 22"/><path d="M2 33 Q16 28 30 33 T58 33 T86 33 T114 33"/>',
    firm:   '<line x1="2" y1="12" x2="108" y2="12"/><line x1="2" y1="22" x2="108" y2="22"/><line x1="2" y1="32" x2="108" y2="32"/>',
    unsure: '<path d="M2 11 Q16 6 30 11 T58 11 T86 11 T114 11" stroke-dasharray="7 6"/><path d="M2 22 Q16 17 30 22 T58 22 T86 22 T114 22" stroke-dasharray="7 6"/><path d="M2 33 Q16 28 30 33 T58 33 T86 33 T114 33" stroke-dasharray="7 6"/>'
  };

  /* ── keyboard support for the radiogroups ────────────────
     Both sections render options as role="radio" buttons. Without
     this, arrow keys do nothing and the group is unusable from the
     keyboard. `onPick` receives the chosen element.
     ──────────────────────────────────────────────────────── */
  function bindRadioKeys(box, selector, onPick) {
    box.addEventListener('keydown', function (e) {
      var keys = ['ArrowRight', 'ArrowDown', 'ArrowLeft', 'ArrowUp', 'Home', 'End'];
      if (keys.indexOf(e.key) < 0) return;
      var items = Array.prototype.slice.call(box.querySelectorAll(selector));
      if (!items.length) return;
      e.preventDefault();
      var at = items.indexOf(document.activeElement);
      var next;
      if (e.key === 'Home') next = 0;
      else if (e.key === 'End') next = items.length - 1;
      else if (e.key === 'ArrowRight' || e.key === 'ArrowDown') next = (at + 1) % items.length;
      else next = (at - 1 + items.length) % items.length;
      items[next].focus();
      if (onPick) onPick(items[next]);
    });
  }

  w.EvayaFinder = {
    FREE_SHIPPING: FREE_SHIPPING,
    SIZE_LABEL: SIZE_LABEL,
    GLYPH: GLYPH,
    questions: questions,
    sizesOf: sizesOf,
    feelsOf: feelsOf,
    idealFeel: idealFeel,
    findVariant: findVariant,
    resolve: resolve,
    addToCart: addToCart,
    bindRadioKeys: bindRadioKeys
  };
})(window);
